import { Typography } from "@/components/common/Typography/Typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type { DataRoomFolder } from "@/types/dataRoom"
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react"
import { DataRoomFolderMenu } from "./DataRoomFolderGrid"

interface DataRoomFolderHeaderProps {
  folder: DataRoomFolder
  /** Root-to-current chain, the current folder last. */
  breadcrumb: DataRoomFolder[]
  hasContent: boolean
  onNavigate: (folderId: string | null) => void
  onRequestCreate: (parentId: string | null) => void
  onRequestRename: (folder: DataRoomFolder) => void
  onRequestDelete: (folder: DataRoomFolder) => void
}

/** Inside a folder: one clickable path (root and ancestors), the folder
 * itself as the title, its description exactly once, and its menu. */
export const DataRoomFolderHeader: React.FC<DataRoomFolderHeaderProps> = ({
  folder,
  breadcrumb,
  hasContent,
  onNavigate,
  onRequestCreate,
  onRequestRename,
  onRequestDelete,
}) => {
  const { translate } = useTranslation()
  const parentId =
    breadcrumb.length > 1 ? breadcrumb[breadcrumb.length - 2].id : null
  const crumbClass =
    "rounded px-1 transition-colors hover:text-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"

  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0 flex-1">
        <nav
          aria-label={translate("DATA_ROOM/BREADCRUMB_ARIA")}
          className="text-grey5 mb-3 flex flex-wrap items-center gap-1"
        >
          <button
            type="button"
            aria-label={translate("DATA_ROOM/BACK_ARIA")}
            onClick={() => onNavigate(parentId)}
            className="hover:bg-grey2 focus-visible:ring-ring -ml-1.5 flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:text-white focus-visible:ring-2 focus-visible:outline-hidden"
          >
            <IconChevronLeft size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => onNavigate(null)}
            className={crumbClass}
          >
            <Typography as="span" variant="textS" className="text-inherit!">
              {translate("DATA_ROOM/TITLE")}
            </Typography>
          </button>
          {breadcrumb.slice(0, -1).map((ancestor) => (
            <span key={ancestor.id} className="flex items-center gap-1">
              <IconChevronRight size={12} aria-hidden="true" />
              <button
                type="button"
                onClick={() => onNavigate(ancestor.id)}
                className={crumbClass}
              >
                <Typography as="span" variant="textS" className="text-inherit!">
                  {ancestor.name}
                </Typography>
              </button>
            </span>
          ))}
        </nav>
        <Typography as="h2" variant="titleM" className="wrap-break-word">
          {folder.name}
        </Typography>
        {folder.description && (
          <Typography variant="textS" className="text-grey5 mt-1">
            {folder.description}
          </Typography>
        )}
      </div>
      <DataRoomFolderMenu
        folder={folder}
        hasContent={hasContent}
        onRequestCreate={onRequestCreate}
        onRequestRename={onRequestRename}
        onRequestDelete={onRequestDelete}
      />
    </div>
  )
}
